import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import TextFieldWithValidation from "../components/TextFieldWithValidation";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CircularProgress } from "@mui/material";

// Country codes list (sample)
const countryCodes = [
  { code: "+91", name: "India" },
  { code: "+1", name: "United States" },
  { code: "+44", name: "United Kingdom" },
  { code: "+61", name: "Australia" },
  { code: "+81", name: "Japan" },
];

const LoginSignup = () => {
  const [mode, setMode] = useState("login"); // "login" or "signup"
  const [isUsingOTP, setIsUsingOTP] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("+91");
  const [mobile, setMobile] = useState("");
  const [pin, setPin] = useState("");
  const [isSendingOTP, setIsSendingOTP] = useState(false); // State to manage sending OTP
  const [otpSent, setOtpSent] = useState(false); // To track if OTP is sent

  const validatePin = (value) => /^[0-9]{0,6}$/.test(value); // Allows only 0-6 digits

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Reset form fields when mode changes
  useEffect(() => {
    setFirstName("");
    setLastName("");
    setCountry("+91");
    setMobile("");
    setPin("");
    setIsUsingOTP(false);
    setIsSendingOTP(false);
    setOtpSent(false); // Reset OTP sent status when switching modes
  }, [mode]);

  const handleModeChange = (event, newMode) => {
    if (newMode) setMode(newMode);
  };

  const handleSendOTP = () => {
    // Simulate sending OTP with a delay
    setIsSendingOTP(true);
    setTimeout(() => {
      setIsSendingOTP(false); // Reset after OTP is sent
      setIsUsingOTP(true); // Enable OTP input
      setOtpSent(true); // Mark OTP as sent
    }, 2000); // Simulate a delay for sending OTP (2 seconds)
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#f5f5f5",
        padding: isMobile ? 2 : 4,
      }}
    >
      <Box
        sx={{
          width: isMobile ? "100%" : 400,
          maxWidth: 400,
          backgroundColor: "white",
          padding: 3,
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        {/* Toggle Buttons */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          fullWidth
          sx={{ marginBottom: 2 }}
        >
          <ToggleButton value="login" sx={{ borderRadius: "16px 0 0 16px", textTransform: "none" }}>
            Login
          </ToggleButton>
          <ToggleButton value="signup" sx={{ borderRadius: "0 16px 16px 0", textTransform: "none" }}>
            Signup
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Signup Form */}
        {mode === "signup" && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  label="Country"
                >
                  {countryCodes.map(({ code, name }) => (
                    <MenuItem key={code} value={code}>
                      {code} ({name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                label="Mobile Number"
                variant="outlined"
                required
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                fullWidth
              />
            </Grid>
            
            {/* Send OTP Button */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                disabled={isSendingOTP}
                onClick={handleSendOTP}
              >
                {isSendingOTP ? <CircularProgress size={24} /> : otpSent ? "Re-send OTP" : "Send OTP"}
              </Button>
            </Grid>
            
            {/* OTP input box */}
            {isUsingOTP && (
              <Grid item xs={12}>
                <TextFieldWithValidation
                  label="Enter OTP"
                  type="password"
                  value={pin}
                  setValue={setPin}
                  validate={validatePin}
                  showEye
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Button variant="contained" color="primary" fullWidth>
                Signup
              </Button>
            </Grid>
          </Grid>
        )}

        {/* Login Form */}
        {mode === "login" && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  label="Country"
                >
                  {countryCodes.map(({ code, name }) => (
                    <MenuItem key={code} value={code}>
                      {code} ({name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                label="Mobile Number"
                variant="outlined"
                required
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                fullWidth
              />
            </Grid>

            {/* Conditionally show OTP button based on OTP usage */}
            {isUsingOTP && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled={isSendingOTP}
                  onClick={handleSendOTP}
                >
                  {isSendingOTP ? <CircularProgress size={24} /> : otpSent ? "Re-send OTP" : "Send OTP"}
                </Button>
              </Grid>
            )}

            {/* OTP input box */}
            {isUsingOTP && (
              <Grid item xs={12}>
                <TextFieldWithValidation
                  label="Enter OTP"
                  type="password"
                  value={pin}
                  setValue={setPin}
                  validate={validatePin}
                  showEye
                />
              </Grid>
            )}

            {/* PIN input box */}
            {!isUsingOTP && (
              <Grid item xs={12}>
                <TextFieldWithValidation
                  label="Enter PIN"
                  type="password"
                  value={pin}
                  setValue={setPin}
                  validate={validatePin}
                  showEye
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{ cursor: "pointer", color: "blue" }}
                onClick={() => setIsUsingOTP(!isUsingOTP)}
              >
                {isUsingOTP ? "Login using PIN" : "Use OTP instead"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" color="primary" fullWidth>
                Login
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default LoginSignup;
